// src/utils/notification.utils.ts
// Utility helpers for notification routing and sound effects.

/**
 * Resolves raw backend actionLink or notification metadata into a clean frontend route.
 */
export function resolveNotificationLink(
  actionLink?: string | null,
  title?: string,
  category?: string,
  message?: string
): string {
  let link = actionLink?.trim() || ''

  // 1. Strip full origin if full URL
  if (link.startsWith('http://') || link.startsWith('https://')) {
    try {
      const url = new URL(link)
      link = url.pathname + url.search + url.hash
    } catch {
      // Fallback
    }
  }

  // 2. Strip API prefixes if backend sent them
  if (link.startsWith('/api/v1')) {
    link = link.replace('/api/v1', '')
  } else if (link.startsWith('/api')) {
    link = link.replace('/api', '')
  }

  const text = ((title || '') + ' ' + (category || '') + ' ' + (message || '')).toLowerCase()

  // 3. Fallback matching if link is empty, #, or /
  if (!link || link === '#' || link === '/') {
    if (text.includes('migration')) {
      return '/students?tab=migrations'
    }
    if (text.includes('student')) {
      return '/students'
    }
    if (text.includes('institution')) {
      return '/institutions'
    }
    if (text.includes('exam') || text.includes('result')) {
      return '/exams'
    }
    if (text.includes('certificate')) {
      return '/certificates'
    }
    if (text.includes('payment') || text.includes('ledger') || text.includes('transaction')) {
      return '/payments'
    }
    if (text.includes('commission') || text.includes('payout')) {
      return '/commissions'
    }
    if (text.includes('coupon')) {
      return '/coupons'
    }
    if (text.includes('staff')) {
      return '/staff'
    }
    if (text.includes('report')) {
      return '/reports'
    }
    return '/notifications'
  }

  // 4. Normalize migration sub-routes
  if (link.includes('migration')) {
    return '/students?tab=migrations'
  }

  return link
}

/**
 * Plays a notification sound.
 * Tries custom sound candidates (including notification.mp3 and notification.mp3.mp3 for Windows double-extensions),
 * and falls back seamlessly to Web Audio API synthesis.
 */
export function playNotificationSound(soundUrl?: string) {
  if (typeof window === 'undefined') return
  const isMuted = localStorage.getItem('notification_muted') === 'true'
  if (isMuted) return

  const soundCandidates = soundUrl
    ? [soundUrl]
    : [
        '/sounds/notification.mp3',
        '/sounds/notification.mp3.mp3',
        '/sounds/notification.wav',
      ]

  let played = false

  const tryPlayCandidate = (index: number) => {
    if (index >= soundCandidates.length) {
      if (!played) {
        synthesizeChimeSound()
      }
      return
    }

    const audio = new Audio(soundCandidates[index])
    audio
      .play()
      .then(() => {
        played = true
      })
      .catch(() => {
        tryPlayCandidate(index + 1)
      })
  }

  tryPlayCandidate(0)
}

/**
 * Synthesizes a clean 2-tone chime using Web Audio API (D5 -> A5 harmonics).
 */
function synthesizeChimeSound() {
  try {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!AudioContextClass) return

    const ctx = new AudioContextClass()
    const now = ctx.currentTime

    // Note 1 (D5 - 587.33 Hz)
    const osc1 = ctx.createOscillator()
    const gain1 = ctx.createGain()
    osc1.type = 'sine'
    osc1.frequency.setValueAtTime(587.33, now)
    gain1.gain.setValueAtTime(0.08, now)
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.12)
    osc1.connect(gain1)
    gain1.connect(ctx.destination)
    osc1.start(now)
    osc1.stop(now + 0.12)

    // Note 2 (A5 - 880 Hz) - 80ms delay
    const osc2 = ctx.createOscillator()
    const gain2 = ctx.createGain()
    osc2.type = 'sine'
    osc2.frequency.setValueAtTime(880, now + 0.08)
    gain2.gain.setValueAtTime(0.1, now + 0.08)
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.28)
    osc2.connect(gain2)
    gain2.connect(ctx.destination)
    osc2.start(now + 0.08)
    osc2.stop(now + 0.28)
  } catch {
    // Ignore autoplay restriction errors
  }
}
